import React from "react";
import { Box, Typography, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import {
  EnrollmentTargetKind,
  EnrollmentTargetsState,
  SelectedTargetItem,
} from "../types";
import InitiativeRowCst from "@components/InitiativeRowCst";
import UserRow from "./tabs/rows/UserRow";
import GroupRow from "./tabs/rows/GroupRow";

interface SelectedTargetsRecapProps {
  targets: EnrollmentTargetsState;
}

interface RecapEntry {
  kind: EnrollmentTargetKind;
  item: SelectedTargetItem;
}

const SelectedTargetsRecap: React.FC<SelectedTargetsRecapProps> = ({
  targets,
}) => {
  const { t } = useTranslation("enrollment-modal");
  const theme = useTheme();

  const entries: RecapEntry[] = [
    ...targets.users.map<RecapEntry>((item) => ({ kind: "users", item })),
    ...targets.groups.map<RecapEntry>((item) => ({ kind: "groups", item })),
    ...targets.initiatives.map<RecapEntry>((item) => ({
      kind: "initiatives",
      item,
    })),
  ];

  if (entries.length === 0) return null;

  const renderRow = ({ kind, item }: RecapEntry) => {
    switch (kind) {
      case "users":
        return <UserRow user={item.meta ?? { id_user: item.id }} />;
      case "groups":
        return (
          <GroupRow
            group={
              item.meta ?? {
                id: Number(item.id),
                name: item.label,
                type: item.groupType,
              }
            }
          />
        );
      case "initiatives":
        return (
          <InitiativeRowCst
            initiative={item.meta ?? { id: Number(item.id), name: item.label }}
          />
        );
    }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
      <Typography
        sx={{
          fontSize: "13px",
          color: theme.palette.text.tertiary,
        }}
      >
        {t("recap.selected-count", { count: entries.length }) ||
          `${entries.length} elementi selezionati`}
      </Typography>

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 1.5,
          maxHeight: 220,
          overflowY: "auto",
        }}
      >
        {entries.map(({ kind, item }) => (
          <Box
            key={`${kind}-${String(item.id)}`}
            sx={{
              padding: "12px 16px",
              borderRadius: "10px",
              border: `1px solid ${theme.palette.divider}`,
              backgroundColor: theme.palette.background.paper,
            }}
          >
            {renderRow({ kind, item })}
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default SelectedTargetsRecap;
